// frontend/src/lib/api.js
import axios from "axios";
import router from "../router";

// Create the axios instance
const api = axios.create({
  baseURL: "/api",
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("auth_token");
      if (router.currentRoute.value.path !== "/login") {
        router.push("/login");
      }
    }
    return Promise.reject(error);
  }
);

// Store CRUD operations
export const getStores = () => api.get("/stores-crud/");
export const createStore = (data) => api.post("/stores-crud/", data);
export const updateStore = (id, data) => api.put(`/stores-crud/${id}`, data);
export const deleteStore = (id) => api.delete(`/stores-crud/${id}`);

// Timezones
export const getTimezones = () => api.get("/stores-crud/timezones");

// Store hours updates
export const updateStoreHours = (storeId, hours) =>
  api.patch(`/stores-crud/${storeId}/hours`, hours);

export const updateStoreTimezone = (storeId, timezone) =>
  api.patch(`/stores-crud/${storeId}/timezone`, { timezone });

export const updateStoreWeekStartDay = (storeId, weekStartDay) =>
  api.patch(`/stores-crud/${storeId}/week-start-day`, { week_start_day: weekStartDay });

// Holiday hours CRUD
export const getStoreHolidayHours = (storeId, startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  const query = params.toString();
  return api.get(`/holiday-hours/store/${storeId}${query ? "?" + query : ""}`);
};

export const getHoursForDateRange = (storeId, startDate, endDate) =>
  api.get(`/holiday-hours/store/${storeId}/range?start_date=${startDate}&end_date=${endDate}`);

export const getHoursForDate = (storeId, date) =>
  api.get(`/holiday-hours/store/${storeId}/date/${date}`);

export const createHolidayHours = (data) =>
  api.post("/holiday-hours/", data);

export const updateHolidayHours = (id, data) =>
  api.put(`/holiday-hours/${id}`, data);

export const deleteHolidayHours = (id) =>
  api.delete(`/holiday-hours/${id}`);

// Authentication
export const requestLoginCode = (email) =>
  api.post("/login/request", { email });
export const verifyLoginCode = (email, code) =>
  api.post("/login/verify", { email, code });
export const getCurrentUser = () => api.get("/users/me");

// Staff management
export const getStaff = (includeInactive = true) =>
  api.get(`/users-crud/?only_active=${!includeInactive}`);
export const createStaffMember = (data) => api.post("/users-crud/", data);
export const updateStaffMember = (id, data) =>
  api.put(`/users-crud/${id}`, data);
export const deleteStaffMember = (id, hardDelete = true) =>
  api.delete(`/users-crud/${id}?hard=${hardDelete}`);

// Shift management
// Note: Datetime conversion is handled by useShifts composable - these are thin wrappers
export const getShifts = (params) => api.get("/shifts", { params });
export const createShift = (data) => api.post("/shifts-crud/", data);
export const updateShift = (id, data) => api.put(`/shifts-crud/${id}`, data);
export const deleteShift = (id) => api.delete(`/shifts-crud/${id}`);
export const checkShiftConflicts = (params) => api.get("/shifts/check-conflicts", { params });

// Availability management
// Note: Datetime conversion is handled by useAvailability composable - these are thin wrappers
export const getAvailabilities = (params) => api.get("/availabilities/", { params });
export const createAvailability = (data) => api.post("/availabilities/", data);
export const updateAvailability = (id, data) => api.put(`/availabilities/user/${id}`, data);
export const deleteAvailability = (id) => api.delete(`/availabilities/user/${id}`);

// Check if time-off conflicts with existing shifts
export const checkTimeOffShiftConflicts = (params) =>
  api.post("/availabilities/check-shift-conflicts", null, { params });

// Bulk delete shifts
export const deleteShiftsBulk = (shiftIds) =>
  api.delete("/shifts/bulk", { data: shiftIds });

// Email
export const sendScheduleEmail = (params) => api.post("/email/schedule", params);

// Auto-schedule endpoints
export const generateSchedule = (data) => api.post("/schedule/generate", data);
export const applySchedule = (data) => api.post("/schedule/apply", data);

// Admin tools
export const exportDb = () => api.get("/admin/export");
export const importDb = (data) => api.post("/admin/import", data);

// Export the API instance for direct use
export default api;
